import React, { useState } from 'react'
import { useFormik, FormikProvider, FieldArray, getIn } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Button, Col, Row } from 'react-bootstrap'

interface Props {
  closeOpenAIModal2: () => void
}

interface Configuration {
  name: string
  apikey: string
}

const initialValues = {
  configurations: [{ name: '', apikey: '' }] as Configuration[]
}

const sharepointSchema = Yup.object().shape({
  configurations: Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .min(3, 'Minimum 3 symbols')
        .max(50, 'Maximum 50 symbols')
        .required('Name is required'),
      apikey: Yup.string()
        .min(3, 'Minimum 3 symbols')
        .max(50, 'Maximum 50 symbols')
        .required('API Key is required')
    })
  )
})

const OpenAIModal: React.FC<Props> = ({ closeOpenAIModal2 }) => {
  const [loadingOL, setLoading] = useState(false)

  const formik = useFormik({
    initialValues,
    validationSchema: sharepointSchema,
    onSubmit: (values) => {
      // Handle form submission
      console.log(values)
    }
  })

  return (
    <div className='card w-600px h-600 mb-4 p-2 position-relative text-center'>
      <button
        type='button'
        className='btn-close position-absolute top-0 end-0 m-n4 rounded-circle'
        aria-label='Close'
        onClick={closeOpenAIModal2}
        style={{
          backgroundColor: 'rgba(218, 222, 243)',
          height: '25px',
          width: '25px',
          color: 'rgba(52, 60, 106)'
        }}
      ></button>
      <div className='d-flex justify-content-between align-items-center px-3 py-2'>
        <h2 className='mb-0'>Gen AI API Models Configuration</h2>
      </div>

      <div className='d-flex justify-content-between align-items-center px-3 py-2'>
        <p className='mb-0'>Fill all the details given in the form.</p>
      </div>

      <FormikProvider value={formik}>
        <form
          onSubmit={formik.handleSubmit}
          className='d-flex flex-column mb-10 fv-row fv-plugins-icon-container'
        >
          <FieldArray name='configurations'>
            {({ push, remove }) => (
              <>
                {formik.values.configurations.map((_, index) => (
                  <div key={index}>
                    <Row>
                      <Col md={6} className='mb-3'>
                        <div className='d-flex align-items-start mt-6 px-3 py-2'>
                          <label className='fs-6 fw-bold mb-2'>Name:</label>
                        </div>
                        <select
                          className='form-select form-select-solid form-select'
                          {...formik.getFieldProps(`configurations.${index}.name`)}
                        >
                          <option key='blankKey' hidden>
                            Select
                          </option>
                          <option value='openai'>OpenAI</option>
                          <option value='azure'>Azure</option>
                        </select>
                        {getIn(formik.touched, `configurations.${index}.name`) &&
                        getIn(formik.errors, `configurations.${index}.name`) ? (
                          <div className='d-flex align-items-start mt-1 n2 px-3 py-2'>
                            <div className='fv-plugins-message-container'>
                              <div className='fv-help-block'>
                                <span role='alert'>
                                  {getIn(formik.errors, `configurations.${index}.name`)}
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </Col>

                      <Col md={6} className='mb-3'>
                        <div className='d-flex align-items-start mt-6 px-3 py-2'>
                          <label className='fs-6 fw-bold mb-2'>API Key:</label>
                        </div>
                        <input
                          placeholder='Enter API key'
                          type='text'
                          autoComplete='off'
                          {...formik.getFieldProps(`configurations.${index}.apikey`)}
                          className={clsx(
                            'form-control form-control-lg form-control-solid',
                            {
                              'is-invalid':
                                getIn(formik.touched, `configurations.${index}.apikey`) &&
                                getIn(formik.errors, `configurations.${index}.apikey`)
                            },
                            {
                              'is-valid':
                                getIn(formik.touched, `configurations.${index}.apikey`) &&
                                !getIn(formik.errors, `configurations.${index}.apikey`)
                            }
                          )}
                        />
                        {getIn(formik.touched, `configurations.${index}.apikey`) &&
                        getIn(formik.errors, `configurations.${index}.apikey`) ? (
                          <div className='d-flex align-items-start mt-1 n2 px-3 py-2'>
                            <div className='fv-plugins-message-container'>
                              <div className='fv-help-block'>
                                <span role='alert'>
                                  {getIn(formik.errors, `configurations.${index}.apikey`)}
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </Col>
                    </Row>

                    <Row className='mt-4'>
                      <Col className='d-flex justify-content-end'>
                        {formik.values.configurations.length > 1 && (
                          <Button
                            type='button'
                            id='delete-button'
                            variant='secondary'
                            className='d-flex me-2'
                            size='sm'
                            onClick={() => remove(index)}
                          >
                            <i className='bi bi-trash fs-3 p-0 m-0 text-danger' />
                            <span className='ms-2'>Delete</span>
                          </Button>
                        )}
                        {index === formik.values.configurations.length - 1 && (
                          <Button
                            type='button'
                            id='insert-button'
                            variant='secondary'
                            className='d-flex'
                            size='sm'
                            onClick={() => push({ name: '', apikey: '' })}
                          >
                            <i className='bi bi-plus fs-3 p-0 m-0 text-primary' />
                            <span>Add</span>
                          </Button>
                        )}
                      </Col>
                    </Row>
                  </div>
                ))}
              </>
            )}
          </FieldArray>

          <div className='text-start mt-4'>
            <button
              type='submit'
              id='kt_sign_up_submit'
              className='btn btn-lg btn-primary w-100 mb-5'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {!loadingOL && <span className='indicator-label'>Submit</span>}
              {loadingOL && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </FormikProvider>
    </div>
  )
}

export default OpenAIModal
